"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Progress } from "@/components/ui/progress";

export const AttendanceCard = () => {
  const { t } = useTranslation(["workspaces", "common", "calendar"]);

  // Mock attendance data
  const attendanceData = {
    totalDays: 0,
    attendedDays: 0,
    percentage: 0,
    streak: 0,
    thisWeek: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
  };

  const weekDays = [
    { key: "monday", label: t("calendar:days.mon"), attended: attendanceData.thisWeek.monday },
    { key: "tuesday", label: t("calendar:days.tue"), attended: attendanceData.thisWeek.tuesday },
    {
      key: "wednesday",
      label: t("calendar:days.wed"),
      attended: attendanceData.thisWeek.wednesday,
    },
    { key: "thursday", label: t("calendar:days.thu"), attended: attendanceData.thisWeek.thursday },
    { key: "friday", label: t("calendar:days.fri"), attended: attendanceData.thisWeek.friday },
    { key: "saturday", label: t("calendar:days.sat"), attended: attendanceData.thisWeek.saturday },
    { key: "sunday", label: t("calendar:days.sun"), attended: attendanceData.thisWeek.sunday },
  ];

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none p-4 backdrop-blur-xl sm:p-6"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.6 }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-600" />
      </div>

      <div className="relative">
        <div className="mb-3 flex items-center gap-2 sm:mb-4 sm:gap-3">
          <Icon
            className="text-accent-foreground"
            height="1.25rem"
            icon="mdi:calendar-check"
            width="1.25rem"
          />
          <h3 className="text-accent-foreground text-base font-semibold sm:text-lg">
            {t("workspaces:dashboard.attendance.title")}
          </h3>
        </div>

        <div className="mb-4 text-center sm:mb-6">
          <div className="text-accent-foreground mb-1 text-2xl font-bold sm:mb-2 sm:text-3xl">
            {attendanceData.percentage}%
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm">
            {t("workspaces:dashboard.attendance.attendanceRate")}
          </p>
          <Progress className="mt-2 h-1.5 sm:mt-3 sm:h-2" value={attendanceData.percentage} />
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3 sm:mb-4 sm:gap-4">
          <div className="bg-muted/20 rounded-lg p-2.5 text-center sm:rounded-xl sm:p-3">
            <div className="text-accent-foreground mb-0.5 text-lg font-bold sm:mb-1 sm:text-xl">
              {attendanceData.attendedDays}
            </div>
            <p className="text-muted-foreground text-[0.65rem] leading-tight sm:text-xs">
              {t("workspaces:dashboard.attendance.daysAttended")}
            </p>
          </div>
          <div className="bg-muted/20 rounded-lg p-2.5 text-center sm:rounded-xl sm:p-3">
            <div className="text-accent-foreground mb-0.5 text-lg font-bold sm:mb-1 sm:text-xl">
              {attendanceData.streak}
            </div>
            <p className="text-muted-foreground text-[0.65rem] leading-tight sm:text-xs">
              {t("workspaces:dashboard.attendance.dayStreak")}
            </p>
          </div>
        </div>

        <div>
          <p className="text-muted-foreground mb-2 text-xs sm:mb-3 sm:text-sm">
            {t("workspaces:dashboard.attendance.thisWeek")}
          </p>
          <div className="flex justify-between gap-1 sm:gap-2">
            {weekDays.map((day) => (
              <div
                key={day.key}
                className={`flex h-7 w-7 items-center justify-center rounded-md text-[0.65rem] font-medium transition-colors sm:h-8 sm:w-8 sm:rounded-lg sm:text-xs ${
                  day.attended
                    ? "bg-green-500/20 text-green-400"
                    : "bg-muted/30 text-muted-foreground"
                }`}
              >
                {day.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
